import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {stringFilterMatches} from 'common/util/string';
import {defaultOrdering, mapArguments} from 'common/util/function';
import 'common/components/tag-input';
import 'common/components/items-menu';

import css from './index.styl?inline';
import template from './index.jade';

const MIN_INPUT_WIDTH = `12px`;

export class TagSelector extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'all-tags': {type: `json`, description: `DEPRECATED: interface:string[] list of non read only tags`},
      error: `boolean`,
      'input-text': `json`,
      'max-tag-input-height': {type: `string`, default: `280px`},
      'max-tag-items-height': {type: `string`, default: `280px`},
      'min-tags': {type: `number`, default: 1},
      options: {type: `json`, description: `interface: SectionItem[]`},
      'read-only': `boolean`,
      'read-only-tags': {
        type: `json`,
        description: `DEPRECATED: interface:string[] list of read only tags. Use options with isDisabled item property instead`,
      },
      saving: `boolean`,
      'selected-tags': {
        type: `json`,
        description: `DEPRECATED: interface:string[] list of tags to be preselected (displayed in the input box). Use options with isSelected item property instead`,
      },
      'static-tags': {
        type: `boolean`,
        description: `Remove capability to add new tags but keep component editable ("read-only" removes save button)`,
      },
    };
  }

  get config() {
    return {
      defaultState: {
        activeTagIndex: -1,
        options: [],
        inputText: ``,
        inputWidth: MIN_INPUT_WIDTH,
        matchingTags: [],
        selectedTags: new Set(),
      },
      css,
      template,
      helpers: {
        showCreateButton: () => {
          const isNewTag =
            this.state.inputText && !this.state.options.filter((tag) => tag.value === this.state.inputText).length;
          return isNewTag && !this.attr(`read-only`) && !this.attr(`static-tags`);
        },
        addTag: ({item, modifierKeys}) => {
          if (!modifierKeys.alt) {
            this._addTag(item);
          }
        },
        addNewTag: (tag) => this._addNewTag(tag),
        removeTag: (tag) => this._removeTag(tag),
        saveTags: () => {
          if (!this.attr(`saving`)) {
            this.dispatchEvent(new CustomEvent(`save`, {detail: {tags: this.state.selectedTags}}));
          }
        },
        getSelectedTagAttrs: () => {
          return Array.from(this.state.selectedTags).map((tagName) => {
            const tag = this._getTagFromOptions(tagName);
            return {
              label: tag.label,
              isDisabled: tag.isDisabled,
              value: tag.value,
              attrs: {
                icon: tag.icon,
                'tag-name': tag.value,
              },
            };
          });
        },
        focusInput: () => this.focus(),
        handleInputChange: (ev) => {
          switch (ev.detail.action) {
            case `removeTag`:
              this._removeTag(ev.detail.tag);
              break;
            case `text`:
              this.update({inputText: ev.detail.value});
              this._filterTags();
              break;
          }
          this.dispatchEvent(
            new CustomEvent(`change`, {
              detail: {
                action: `updateInput`,
                inputText: this.state.inputText,
                matchingTags: this.state.matchingTags.map((tag) => tag.value),
              },
            }),
          );
        },
        handleKeyDown: (ev) => {
          if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
            if (ev.altKey) {
              this.config.helpers.saveTags();
            } else if (this.state.matchingTags.length === 0) {
              this._addNewTag(this.state.inputText);
            }
          }
        },
        getKeydownProxyEl: () => this.el.querySelector(`.input-el`) || this.el,
        searchText: () => {
          return this._inputText(`search`, `Search dashboards...`);
        },
        emptyText: () => {
          return this._inputText(`empty`, `No dashboards found.`);
        },
        createNewText: () => {
          return this._inputText(`create`, `Create new dashboard for:`);
        },
        getMenuSections: () => [{items: this.state.matchingTags}],
      },
      useShadowDom: true,
    };
  }

  connectedCallback() {
    super.connectedCallback(...arguments);
    this._readTags();
    this._filterTags();
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    if (this.initialized) {
      switch (attr) {
        case `all-tags`:
        case `options`:
        case `read-only-tags`:
        case `selected-tags`:
          this.update({options: this._getOptions()});
          this._filterTags();
          break;
      }
    }
  }

  _readTags() {
    this.update({
      options: this._getOptions(),
    });
  }

  _getTagIfExists(tag) {
    const currentTag = this.state.options.find((t) => t.value === tag.value);
    return currentTag ? {...currentTag} : tag;
  }

  _selectTag(tag) {
    this.state.selectedTags.add(tag.value);
    const newTag = this._getTagIfExists(tag);
    newTag.isSelected = true;
    this._updateOptionsTag(newTag);
  }

  _deselectTag(tag) {
    this.state.selectedTags.delete(tag.value);
    const newTag = this._getTagIfExists(tag);
    newTag.isSelected = false;
    this._updateOptionsTag(newTag);
  }

  _getSelectedTags() {
    return Array.from(this.state.options).filter((tag) => tag.isSelected);
  }

  _getOptions() {
    let options = [];
    if (this.attr(`all-tags`)) {
      // Deprecated path: We used to use the `all-tags` attr and combine it with `selected-tags` and `read-only-tags`
      // but decided it was better to just have one `options` attr that combines all of that into one object.
      // We can delete this entire if-block once the codebase no longer uses all-tags, along with said attrs
      const readonlyTags = new Set(this.attr(`read-only-tags`));
      this.update({selectedTags: new Set(this.attr(`selected-tags`))});
      options = this.attr(`all-tags`)
        .sort(mapArguments(defaultOrdering, (a) => a.toLowerCase()))
        .map((tag) => {
          return {
            isDisabled: readonlyTags.has(tag),
            isSelected: this.state.selectedTags.has(tag),
            label: tag,
            value: tag,
          };
        });
    } else if (this.attr(`options`)) {
      this.update({
        selectedTags: new Set(
          this.attr(`options`)
            .filter((option) => option.isSelected)
            .map((option) => option.value),
        ),
      });
      this.attr(`options`).forEach((tag) => {
        tag.value = tag.value || tag.label;
        options.push(tag);
      });
    } else {
      // Do nothing if nothing was passed in
    }
    return options;
  }

  _filterTags() {
    const search = this.state.inputText.trim().toLowerCase();
    //Here to support deprecated functionality of return matchingTags in the updateInput event
    this.state.matchingTags = Array.from(this.state.options).filter(
      (tag) => !tag.isSelected && (stringFilterMatches(tag.value, search) || stringFilterMatches(tag.label, search)),
    );
    this.update();
  }

  _removeTag(tag) {
    this._deselectTag(tag);
    this.update({inputText: ``, inputWidth: MIN_INPUT_WIDTH});
    this._filterTags();
    requestAnimationFrame(() => this.focus());
    this.dispatchEvent(new CustomEvent(`change`, {detail: {tagName: tag.value, action: `removeTag`}}));
  }

  _addNewTag(tagName) {
    const tag = {label: tagName, value: tagName};
    this._addTag(tag);
  }

  _addTag(tag) {
    if (tag.value && !tag.isSelected) {
      this._selectTag(tag);
      this.update({inputText: ``, inputWidth: MIN_INPUT_WIDTH});
      this._filterTags();
      requestAnimationFrame(() => this.focus());
      this.dispatchEvent(new CustomEvent(`change`, {detail: {tagName: tag.value, action: `addTag`}}));
    }
  }

  _inputText(field, defaultText) {
    const inputText = this.attr(`input-text`);
    return inputText && inputText[field] ? inputText[field] : defaultText;
  }

  _getTagFromOptions(value) {
    return this.state.options.find((tag) => tag.value === value) || {};
  }

  _updateOptionsTag(newTag) {
    let newTagAdded = false;
    const newOptions = this.state.options.map((tag) => {
      if (tag.value === newTag.value) {
        newTagAdded = true;
        return newTag;
      }
      return tag;
    });

    if (!newTagAdded) {
      newOptions.push(newTag);
    }

    this.update({options: newOptions});
  }
}

export const TagSelectorEl = defineAndMakeJsxFactory(`mp-tag-selector`, TagSelector);
